import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import "./SettingsVisualAid.css";

function PreviewShell({ accent, children, wide }) {
  return (
    <div className="sva-preview" style={{ "--sva-accent": accent }}>
      <div className={`sva-phone${wide ? " sva-phone-wide" : ""}`}>{children}</div>
    </div>
  );
}

function ProductTile({ compact }) {
  return (
    <div className={`sva-tile${compact ? " is-compact" : ""}`}>
      <span className="sva-tile-img" />
      <span className="sva-tile-copy">
        <i />
        <b />
      </span>
    </div>
  );
}

function HorizontalPreview({ accent }) {
  return (
    <PreviewShell accent={accent}>
      <div className="sva-notch" />
      <div className="sva-screen">
        <div className="sva-logo" />
        <div className="sva-cat-title" />
        <div className="sva-products-h">
          <div className="sva-products-h-track">
            <ProductTile compact />
            <ProductTile compact />
            <ProductTile compact />
            <ProductTile compact />
          </div>
        </div>
      </div>
    </PreviewShell>
  );
}

function VerticalPreview({ accent }) {
  return (
    <PreviewShell accent={accent}>
      <div className="sva-notch" />
      <div className="sva-screen">
        <div className="sva-logo" />
        <div className="sva-cat-title" />
        <div className="sva-products-v">
          <ProductTile />
          <ProductTile />
          <ProductTile />
        </div>
      </div>
    </PreviewShell>
  );
}

function MenuModePreview({ accent, ordering }) {
  return (
    <PreviewShell accent={accent}>
      <div className="sva-notch" />
      <div className="sva-screen">
        <div className="sva-logo" />
        <div className="sva-line short" />
        <div className="sva-product">
          <span className={`sva-plus${ordering ? " is-on" : ""}`} />
        </div>
        <div className="sva-product">
          <span className={`sva-plus${ordering ? " is-on" : ""}`} />
        </div>
        <div className={`sva-cart${ordering ? " is-on" : ""}`} />
      </div>
    </PreviewShell>
  );
}

function Figure({ kind, label }) {
  return (
    <div className={`sva-fig sva-fig-${kind}`}>
      <span className="sva-fig-head" />
      <span className="sva-fig-body" />
      {kind === "waiter" ? <span className="sva-fig-tray" /> : null}
      {label ? <span className="sva-fig-name">{label}</span> : null}
    </div>
  );
}

function SelfServicePreview({ accent }) {
  const { t } = useTranslation();
  return (
    <PreviewShell accent={accent} wide>
      <div className="sva-notch" />
      <div className="sva-floor sva-floor-self">
        <div className="sva-spot sva-spot-counter">
          <span className="sva-spot-bar" />
          <span className="sva-spot-name">{t("visualAidCounter")}</span>
        </div>
        <span className="sva-path sva-path-up" />
        <Figure kind="client" label={t("visualAidClient")} />
      </div>
    </PreviewShell>
  );
}

function TableServicePreview({ accent }) {
  const { t } = useTranslation();
  return (
    <PreviewShell accent={accent} wide>
      <div className="sva-notch" />
      <div className="sva-floor sva-floor-table">
        <div className="sva-spot sva-spot-table">
          <span className="sva-spot-round" />
          <Figure kind="seated" />
          <span className="sva-spot-name">{t("visualAidTable")}</span>
        </div>
        <span className="sva-path sva-path-side" />
        <Figure kind="waiter" label={t("visualAidWaiter")} />
      </div>
    </PreviewShell>
  );
}

function TaxPreview({ accent, included }) {
  return (
    <div className="sva-preview" style={{ "--sva-accent": accent }}>
      <div className="sva-receipt">
        <div className="sva-receipt-row">
          <i />
          <b />
        </div>
        <div className="sva-receipt-row">
          <i />
          <b />
        </div>
        <div className="sva-receipt-tax">
          {included ? "TAX INCL." : "+ TAX"}
        </div>
      </div>
    </div>
  );
}

function StaticMenuPreview({ accent }) {
  return (
    <PreviewShell accent={accent}>
      <div className="sva-notch" />
      <div className="sva-screen sva-screen-static">
        <div className="sva-static-sheet" />
      </div>
    </PreviewShell>
  );
}

const PREVIEW_MAP = {
  horizontal: HorizontalPreview,
  vertical: VerticalPreview,
  view: (props) => <MenuModePreview {...props} ordering={false} />,
  ordering: (props) => <MenuModePreview {...props} ordering />,
  self: SelfServicePreview,
  table: TableServicePreview,
  taxExcluded: (props) => <TaxPreview {...props} included={false} />,
  taxIncluded: (props) => <TaxPreview {...props} included />,
  interactive: (props) => <MenuModePreview {...props} ordering />,
  staticMenu: StaticMenuPreview,
};

export function VisualChoiceGrid({
  label,
  value,
  onChange,
  options,
  accent = "#fa8072",
  disabled = false,
}) {
  const { t } = useTranslation();

  return (
    <div className={`sva-block${disabled ? " is-disabled" : ""}`}>
      {label ? <label className="form-label text-primary">{label}</label> : null}
      <div className="sva-grid" role="radiogroup" aria-label={typeof label === "string" ? label : undefined}>
        {options.map((opt) => {
          const selected = value === opt.value;
          const Preview = PREVIEW_MAP[opt.preview];
          return (
            <button
              key={opt.value}
              type="button"
              className={`sva-card${selected ? " is-selected" : ""}`}
              onClick={() => {
                if (!disabled) onChange(opt.value);
              }}
              aria-pressed={selected}
              disabled={disabled}
            >
              {Preview ? <Preview accent={accent} /> : <div className="sva-preview" />}
              <div className="sva-meta">
                <strong>{opt.title}</strong>
                {opt.hint ? <span>{opt.hint}</span> : null}
              </div>
              <span className="sva-select">
                {selected ? t("visualAidSelected") : t("visualAidSelect")}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function ToggleVisualAid({ kind, on = false, accent = "#fa8072" }) {
  return (
    <div
      className={`sva-mini sva-mini-${kind}${on ? " is-on" : " is-off"}`}
      style={{ "--sva-accent": accent }}
      aria-hidden
    >
      {(kind === "welcome" || kind === "video") && (
        <div className="sva-mini-pages">
          <div className={`sva-mini-page is-first${on || kind === "video" ? " is-active" : ""}`}>
            <span className="sva-mini-badge">1</span>
            {kind === "video" && on ? <span className="sva-mini-play" /> : <span className="sva-mini-logo" />}
          </div>
          <div className={`sva-mini-page is-menu${kind === "welcome" && !on ? " is-active" : ""}`}>
            <span className="sva-mini-badge">2</span>
            <span className="sva-mini-line" />
            <span className="sva-mini-line" />
          </div>
        </div>
      )}
      {kind === "logo" && (
        <>
          <span className={`sva-mini-logo${on ? "" : " is-hide"}`} />
          <span className="sva-mini-line" />
          <span className="sva-mini-line short" />
        </>
      )}
      {kind === "onePage" && (
        <>
          <span className={`sva-mini-tabs${on ? " is-hide" : ""}`}>
            <i className="is-on" />
            <i />
          </span>
          <span className="sva-mini-line" />
          <span className="sva-mini-line" />
          {on ? <span className="sva-mini-line" /> : null}
        </>
      )}
      {kind === "claim" && (
        <>
          <span className="sva-mini-line" />
          <span className="sva-mini-line" />
          <span className={`sva-mini-bar-dot${on ? " is-on" : ""}`} />
        </>
      )}
      {kind === "voice" && (
        <>
          <span className="sva-mini-line" />
          <span className={`sva-mini-voice${on ? " is-on" : ""}`} />
        </>
      )}
      {(kind === "contact" || kind === "story" || kind === "events" || kind === "book") && (
        <div className="sva-mini-page is-first is-active">
          <span className="sva-mini-badge">1</span>
          <span className={`sva-mini-chip${on ? " is-on" : ""}`} />
        </div>
      )}
      {kind === "images" && (
        <>
          <span className={`sva-mini-photo${on ? " is-on" : ""}`} />
          <span className="sva-mini-line" />
          <span className="sva-mini-line short" />
        </>
      )}
      {kind === "prices" && (
        <>
          <span className="sva-mini-line" />
          <span className={`sva-mini-price${on ? " is-on" : ""}`} />
        </>
      )}
      {kind === "search" && (
        <>
          <span className={`sva-mini-search${on ? " is-on" : ""}`} />
          <span className="sva-mini-line" />
        </>
      )}
      {kind === "summary" && (
        <>
          <span className={`sva-mini-summary${on ? " is-on" : ""}`}>
            <i />
            <i />
            <i />
          </span>
        </>
      )}
    </div>
  );
}

export function ClientMenuLivePreview({
  restaurantData,
  menuDisplay,
  accent = "#fa8072",
  focusPage,
}) {
  const { t } = useTranslation();
  const style = "horizontal";
  const ordering = restaurantData?.menuMode === "ordering";
  const showLogo = restaurantData?.clientMenuShowLogo !== false;
  const welcome = restaurantData?.clientWelcomeScreenEnabled !== false;
  const video = restaurantData?.clientWelcomeVideoEnabled !== false;
  const voice = restaurantData?.clientMenuVoiceEnabled === true;
  const showClaim = restaurantData?.clientShowClaimButton !== false;
  const showContact = restaurantData?.clientShowWelcomeContactButton !== false;
  const showStory = restaurantData?.clientShowStorytellingButton !== false;
  const showEvents = restaurantData?.clientShowEventsButton !== false;
  const showReservation = restaurantData?.clientShowReservationButton !== false;
  const onePage = restaurantData?.clientShowAllMenusOnOnePage === true;
  const showSearch =
    (menuDisplay?.searchBarHidden ?? restaurantData?.searchBarHidden) !== true;

  const [page, setPage] = useState(welcome ? "welcome" : "menu");

  useEffect(() => {
    if (!welcome) setPage("menu");
  }, [welcome]);

  useEffect(() => {
    if (focusPage === "menu") {
      setPage("menu");
      return;
    }
    if (focusPage === "welcome" && welcome) {
      setPage("welcome");
    }
  }, [focusPage, welcome]);

  const welcomeButtons = [
    { key: "contact", on: showContact, label: t("visualAidContact") },
    { key: "story", on: showStory, label: t("visualAidStory") },
    { key: "events", on: showEvents, label: t("visualAidEvents") },
    { key: "book", on: showReservation, label: t("visualAidBook") },
  ];

  const showWelcomePage = welcome && page === "welcome";

  return (
    <div className="sva-live-phone-wrap" style={{ "--sva-accent": accent }}>
      <span className="sva-live-label">{t("clientMenuLivePreview")}</span>
      {welcome ? (
        <div className="sva-live-tabs">
          <button
            type="button"
            className={page === "welcome" ? "is-on" : ""}
            onClick={() => setPage("welcome")}
          >
            1 · {t("visualAidWelcomePage")}
          </button>
          <button
            type="button"
            className={page === "menu" ? "is-on" : ""}
            onClick={() => setPage("menu")}
          >
            2 · {t("visualAidMenuPage")}
          </button>
        </div>
      ) : null}
      <div className="sva-live-frame" aria-hidden>
        <div className="sva-live-screen">
          {showWelcomePage ? (
            <div className="sva-welcome-page">
              <div className={`sva-welcome-media${video ? " has-video" : ""}`}>
                {showLogo ? <div className="sva-live-logo" /> : null}
                {video ? <span className="sva-welcome-play" /> : null}
              </div>
              <div className="sva-live-welcome-btns">
                {welcomeButtons.map((btn, index) =>
                  btn.on ? (
                    <span
                      key={btn.key}
                      className="sva-live-btn"
                      style={{ animationDelay: `${index * 80}ms` }}
                    >
                      {btn.label}
                    </span>
                  ) : null
                )}
              </div>
            </div>
          ) : (
            <>
              <div className="sva-live-hero is-menu">
                {showLogo ? <div className="sva-live-logo" /> : <div className="sva-live-logo is-off" />}
                <div className={`sva-live-voice${voice ? " is-on" : ""}`} />
              </div>
              <div className="sva-live-body">
                {showSearch ? <div className="sva-live-search" aria-hidden="true" /> : null}
                {!onePage ? (
                  <div className="sva-live-cats">
                    <span className="sva-live-chip is-on" />
                    <span className="sva-live-chip" />
                    <span className="sva-live-chip" />
                  </div>
                ) : null}
                <div
                  className={`sva-live-products${
                    style === "vertical" ? " is-vertical" : " is-horizontal"
                  }`}
                >
                  <div className={`sva-live-item${ordering ? " has-order" : ""}`} />
                  <div className={`sva-live-item${ordering ? " has-order" : ""}`} />
                  {onePage || style === "horizontal" ? (
                    <div className={`sva-live-item${ordering ? " has-order" : ""}`} />
                  ) : null}
                </div>
              </div>
              <div className="sva-live-bar">
                <span className="sva-live-dot is-claim" />
                <span className={`sva-live-dot${showClaim ? " is-claim" : ""}`} />
                <span className="sva-live-dot" />
              </div>
            </>
          )}
        </div>
      </div>
      <span className="sva-meta" style={{ textAlign: "center", minHeight: 0 }}>
        <span>
          {showWelcomePage
            ? t("visualAidWelcomePageHint")
            : t("visualAidMenuPageHint")}
        </span>
      </span>
    </div>
  );
}

export default VisualChoiceGrid;
